import { ModalStyles, UserPreferences } from '@sharefiledev/sharefile-appshell';
import { Breakpoint, ModalProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { getLanguage, t } from './translate';

export const UnitsOfTime = {
	OneDay: 24 * 60 * 60 * 1000,
	OneHour: 60 * 60 * 1000,
	OneMinute: 60 * 1000,
};

export const TIME = {
	NeverDateString: '9999-12-31T23:59:59.9999999Z',
	ShareFileEpochString: '2005-01-01T00:00:00.0000001Z',
	NoExpirationDateThresholdString: '2100-12-31T23:59:59.9999999Z',
};

type DateTypes = Date | Dayjs | string;

export function formatDate(
	date: DateTypes,
	dateFormatOrPreferences: string | UserPreferences
) {
	let dateFormat: string | undefined;
	if (typeof dateFormatOrPreferences === 'string') {
		dateFormat = dateFormatOrPreferences;
	} else {
		dateFormat = dateFormatOrPreferences.DateFormat;
	}

	return formatWithLocale(date, dateFormat?.toUpperCase());
}

export function formatTime(
	date: DateTypes,
	timeFormatOrPreferences: string | UserPreferences
) {
	let timeFormat: string | undefined;

	if (typeof timeFormatOrPreferences === 'string') {
		timeFormat = timeFormatOrPreferences;
	} else {
		timeFormat = timeFormatOrPreferences.TimeFormat;
	}

	return formatWithLocale(date, normalizeTimeFormat(timeFormat));
}

export function formatDateTime(
	date: DateTypes,
	{ DateFormat, TimeFormat }: UserPreferences
) {
	const dateTimeFormat = `${DateFormat?.toUpperCase()} ${normalizeTimeFormat(
		TimeFormat
	)}`;
	return formatWithLocale(date, dateTimeFormat);
}

export function formatCreationDate(
	date: DateTypes,
	{ DateFormat, TimeFormat }: UserPreferences,
	dateTimeRequired?: boolean
) {
	if (!date) {
		return '';
	}

	const creationDate = dayjs(date).locale(getLanguage());

	if (creationDate.isSame(new Date(), 'day')) {
		return creationDate.format(normalizeTimeFormat(TimeFormat));
	}

	if (dateTimeRequired) {
		return formatDateTime(date, { DateFormat, TimeFormat });
	}
	return creationDate.format(DateFormat?.toUpperCase());
}

export function formatExpirationDate(date: DateTypes, userPreferences: UserPreferences) {
	if (!date) {
		return '';
	}

	if (dayjs(date).isSame(dayjs(TIME.NeverDateString))) {
		return t('javascript:date_time.expires_never');
	}

	return formatCreationDate(date, userPreferences);
}

const localesWithAlternateTime = ['ja'];

export function normalizeTimeFormat(format = 'h:mmt') {
	if (format === 'h:mmt' && localesWithAlternateTime.includes(getLanguage())) {
		return 'A h:mm';
	}
	return format.replace('t', ' A');
}

export function formatWithLocale(
	date: DateTypes,
	format: string | undefined
): string | undefined {
	return dayjs(date).locale(getLanguage()).format(format);
}

/**
 * Returns the default modal props based on screen sizes if supplied
 */
export const getDefaultModalProps: (
	screens?: Partial<Record<Breakpoint, boolean>>,
	isMinimizedIframeEnabled?: boolean
) => Partial<ModalProps> | any = (screens, isMinimizedIframeEnabled) => {
	// When feature flag is removed, can remove any undefined properties
	if (isMinimizedIframeEnabled) {
		return {
			centered: undefined,
			width: screens?.sm ? '444px' : '100vw', // 444px is the minimum width of a modal per design standards
			height: 'auto',
			maskClosable: false,
			footer: null,
			styles: {
				body: {
					height: screens?.sm ? undefined : 'calc(100vh - 56px)',
					display: 'flex',
					flexDirection: 'column',
					gap: '8px',
				} as ModalStyles['body'],
			},
		};
	}

	return {
		centered: true,
		width: screens?.sm ? '579px' : '100vw',
		footer: null,
		maskClosable: false,
		styles: {
			body: {
				height: screens?.sm ? '660px' : 'calc(100vh - 56px)', // third screen experience
				display: 'flex',
				flexDirection: 'column',
				gap: '8px',
			} as ModalStyles['body'],
		},
	};
};
